import { defaultLogger, 音韻地位 } from 'tshet-uinh';

import { 母位置名稱, 轉名稱列表, 轉呼 } from '../utils/常量';
import { cached } from '../utils/helper';

// import { 等韻搭配, 鈍音母 } from './音韻屬性常量';
/** 依可搭配的等列出各韻 */
export const 等韻搭配 = {
  一: [...'冬模泰灰咍魂痕寒豪唐登侯覃談'],
  二: [...'江佳皆夬刪山肴耕咸銜'],
  三: [...'鍾支脂之微魚虞祭廢真臻文殷元仙宵陽清蒸尤幽侵鹽嚴凡'],
  四: [...'齊先蕭青添'],
  一三: [...'東歌'],
  二三: [...'麻庚'],
} as const;
/** 幫見影組聲母，在三等分ABC類 */
export const 鈍音母 = [...'幫滂並明見溪羣疑影曉匣云'] as const;

/**
 * 韻鏡中的一個格位。
 *
 * 包含 (轉號, 上位, 右位) 三要素。可使用該三要素初始化。
 *
 * - 轉號：轉的序號（1–43）。
 * - 上位：在一個轉中，從上向下數的位置（1–16）。
 * - 右位：在一個轉中，從右向左數的位置（1–23）。
 */
class 韻鏡位置 {
  /** 轉的序號（1–43）。 */
  轉號: number;

  /** 在一個轉中，從上向下數的位置（1–16）。 */
  上位: number;

  /** 在一個轉中，從右向左數的位置（1–23）。 */
  右位: number;

  /**
   * 建立一個韻鏡位置。
   *
   * @throws 若任何取值超出韻鏡範圍
   */
  constructor(轉號: number, 上位: number, 右位: number) {
    if (轉號 < 1 || 轉號 > 43) {
      throw new Error('轉號必須在 1 到 43 之間');
    }
    if (上位 < 1 || 上位 > 16) {
      throw new Error('上位必須在 1 到 16 之間');
    }
    if (右位 < 1 || 右位 > 23) {
      throw new Error('右位必須在 1 到 23 之間');
    }
    this.轉號 = 轉號;
    this.上位 = 上位;
    this.右位 = 右位;
  }

  /**
   * 轉的名稱（如「內轉第一圖」）。
  */
  @cached
  get 轉名稱() {
    return `${轉名稱列表[this.轉號 - 1]}圖`;
  }

  /**
   * 坐標的字串表示（如「(1,1,1)」）。
  */
  @cached
  get 坐標() {
    const { 轉號, 上位, 右位 } = this;
    return `(${轉號},${上位},${右位})`;
  }

  /**
   * 韻鏡等（「一二三四」）。
  *
  * 僅由上位決定，與切韻等未必一致。
  */
  @cached
  get 韻鏡等() {
    const { 上位 } = this;
    const 韻鏡等 = ((上位 - 1) % 4) + 1;
    return 韻鏡等;
  }

  /**
   * 韻鏡韻。
   *
   * 與切韻的韻未必一致。
   *
   * 與《韻鏡》標註的韻亦未必一致，因為存在一些特殊情況，例如《韻鏡》原書存在刪、山韻排反的情況，此處均已進行修正。
   */
  @cached
  get 韻() {
    const { 轉號, 上位, 右位 } = this;
    return 轉號上位右位2韻(轉號, 上位, 右位);
  }

  /**
   * 切韻等（「一二三四」）。
   */
  @cached
  get 切韻等() {
    const { 轉號, 上位, 右位, 韻鏡等, 韻 } = this;

    if (轉號 === 6 && 上位 === 12 && 右位 === 7) {
      defaultLogger.log(`韻鏡地位 ${this.坐標}（即「地」字）為特殊情況，對應切韻四等`);
      return '四'; // 「地」字為真四等
    }
    if (轉號 === 29 && 上位 === 4 && 右位 === 5) {
      defaultLogger.log(`韻鏡地位 ${this.坐標}（即「爹」字）為特殊情況，對應切韻四等`);
      return '四'; // 「爹」字為真四等
    }
    if (韻鏡等 === 4 && !等韻搭配.四.includes(韻)) {
      defaultLogger.log(`韻鏡四等本應對應切韻四等，但${韻}韻非四等韻，故為假四等真三等，實際為切韻三等`);
      return '三'; // 假四等真三等
    }
    if (韻鏡等 === 2 && ![...等韻搭配.二, ...等韻搭配.二三].includes(韻)) {
      if (12 < 右位 && 右位 <= 17) {
        defaultLogger.log(`韻鏡二等本應對應切韻二等，但${韻}韻非二等韻，故為假二等真三等，實際為切韻三等`);
        return '三'; // 限定為齒音，假二等真三等
      }
      throw new Error('假二等真三等必須為齒音');
    }
    if (轉號 === 33 && ((右位 === 16 && 韻鏡等 === 2) || (右位 === 14 && (上位 === 10 || 上位 === 14)))) {
      defaultLogger.log(`韻鏡二等本應對應切韻二等，但「生」、「省」、「索」、「㵾」、「柵」為特殊情況，屬於莊三化二，故實際為切韻三等`);
      return '三'; // 「生」、「省」、「索」、「㵾」、「柵」莊三化二
    }

    const 韻鏡等漢字 = [...'一二三四'][韻鏡等 - 1];
    const shouldAdd一般情況Str = 韻鏡等 === 2 || 韻鏡等 === 4;
    defaultLogger.log(`韻鏡${韻鏡等漢字}等對應切韻${韻鏡等漢字}等${shouldAdd一般情況Str ? '（一般情況）' : ''}`);
    return 韻鏡等漢字;
  }

  /**
   * 韻鏡母。
   */
  @cached
  get 母() {
    const { 右位, 韻鏡等, 切韻等 } = this;

    // 幫非組
    if (右位 <= 4) {
      const 母 = [...'幫滂並明'][右位 - 1];
      defaultLogger.log(`${母位置名稱[右位]}，對應${母}母`);
      return 母;
    }

    // 端知組
    if (右位 <= 8) {
      // TODO: is 切韻等 correct? can handle 蛭,17,4,15?
      if (切韻等 === '一' || 切韻等 === '四') {
        const 母 = [...'端透定泥'][右位 - 4 - 1];
        defaultLogger.log(`${母位置名稱[右位]}，且為切韻一四等，對應${母}母`);
        return 母;
      }

      const 母 = [...'知徹澄孃'][右位 - 4 - 1];
      defaultLogger.log(`${母位置名稱[右位]}，且為切韻二三等，對應${母}母`);
      return 母;
    }

    // 見組
    if (右位 <= 12) {
      const 母 = [...'見溪羣疑'][右位 - 8 - 1];
      defaultLogger.log(`${母位置名稱[右位]}，對應${母}母`);
      return 母;
    }

    // 齒音
    if (右位 <= 17) {
      if (韻鏡等 === 1 || 韻鏡等 === 4) {
        const 母 = [...'精清從心邪'][右位 - 12 - 1];
        defaultLogger.log(`${母位置名稱[右位]}，且為韻鏡一四等，對應${母}母`);
        return 母;
      }
      if (韻鏡等 === 3) {
        const 母 = [...'章昌船書常'][右位 - 12 - 1]; // TODO: 常船位置
        defaultLogger.log(`${母位置名稱[右位]}，且為韻鏡三等，對應${母}母`);
        return 母;
      }
      if (韻鏡等 === 2) {
        const 母 = [...'莊初崇生俟'][右位 - 12 - 1];
        defaultLogger.log(`${母位置名稱[右位]}，且為韻鏡二等，對應${母}母`);
        return 母;
      }
      throw new Error('invalid 韻鏡等');
    }

    // 喉音
    if (右位 <= 20) {
      const 母 = [...'影曉匣'][右位 - 17 - 1];
      defaultLogger.log(`${母位置名稱[右位]}，對應${母}母`);
      return 母;
    }

    // 喻母
    if (右位 === 21) {
      if (韻鏡等 === 3) {
        defaultLogger.log(`${母位置名稱[右位]}，且為韻鏡三等，對應云母`);
        return '云';
      }
      defaultLogger.log(`${母位置名稱[右位]}，且非韻鏡三等，對應以母`);
      return '以';
    }

    // 舌齒音
    if (右位 <= 23) {
      const 母 = [...'來日'][右位 - 21 - 1];
      defaultLogger.log(`${母位置名稱[右位]}，對應${母}母`);
      return 母;
    }

    throw new Error('invalid 右位');
  }

  /**
   * 韻鏡呼（開/合/`null`）。
   *
   * 幫滂並明母、模侯尤韻的呼均為 `null`。
   *
   * 與《韻鏡》標註的呼未必一致，例如包含 `null`，又如《韻鏡》原書第四轉標註為「內轉第四開合」，而此處為「開」。
   */
  @cached
  get 呼() {
    const { 轉號, 轉名稱, 韻, 母 } = this;
    if ([...'幫滂並明'].includes(母) || [...'模侯尤'].includes(韻)) {
      return null;
    }
    const 呼 = 轉呼[轉號 - 1];
    if (呼 !== null) {
      defaultLogger.log(`${轉名稱}對應的呼為${呼}口`);
    }
    return 呼;
  }

  /**
   * 聲調。
   *
   * 由上位推得，並根據「去聲寄此」的韻鏡標註修正。
   */
  @cached
  get 聲() {
    const { 轉號, 上位 } = this;
    const raw聲 = [...'平上去入'][Math.floor((上位 - 1) / 4)];
    if ([9, 10, 13, 14].includes(轉號) && raw聲 == '入') {
      defaultLogger.log(`此位置處於入聲位，但第 ${轉號} 轉入聲標註「去聲寄此」，故實際為去聲`);
      return '去'; // 標註「去聲寄此」
    }
    defaultLogger.log(`此位置處於${raw聲}聲位，故為${raw聲}聲`);
    return raw聲;
  }

  /**
   * 切韻類（A/B/C/`null`）。
   */
  @cached
  get 類() {
    const { 韻鏡等, 切韻等, 韻, 母 } = this;
    if (切韻等 !== '三' || !鈍音母.includes(母)) {
      return null;
    }
    if (韻 === '幽') {
      const { 轉號, 上位, 右位 } = this;
      if ([...'幫滂並明'].includes(母) || (轉號 === 37 && 上位 === 4 && 右位 === 10)) {
        defaultLogger.log(`幽韻幫組及「惆」字對應 B 類。注意「飍」、「烋」為 A、B 類對立，「烋」為 B 類，但此處無法區分二者`);
        return 'B'; // 幫組、「惆」為 B 類。注意「飍」、「烋」為 A、B 類對立，「烋」為 B 類，但此處無法區分二者
      }
      defaultLogger.log(`幽韻非幫組且非「惆」字對應 A 類`);
      return 'A';
    }
    if (韻 === '蒸') {
      const { 呼 } = this;
      if ([...'幫滂並明'].includes(母) || 呼 === '合') {
        defaultLogger.log(`蒸韻幫組或合口對應 B 類。注意「憶」、「抑」為 B、C 類對立，「抑」為 B 類，但此處無法區分二者`);
        return 'B'; // 幫組、合口為 B 類。注意「憶」、「抑」為 B、C 類對立，「抑」為 B 類，但此處無法區分二者
      }
      defaultLogger.log(`蒸韻非幫組且非合口對應 C 類`);
      return 'C';
    }
    if (![...'支脂祭真仙宵清侵鹽庚幽'].includes(韻)) {
      defaultLogger.log(`${韻}韻對應 C 類`);
      return 'C';
    }
    if (韻鏡等 === 4) {
      defaultLogger.log(`韻鏡四等對應 A 類（一般情況）`);
      return 'A';
    }
    if (韻鏡等 === 3) {
      defaultLogger.log(`韻鏡三等對應 B 類（一般情況）`);
      return 'B';
    }
    throw new Error('error');
  }

  /**
   * 韻鏡位置描述（如「外轉第四十二圖·舌音第一位·入聲位·韻鏡一等」）。
   */
  @cached
  get 描述() {
    const { 上位, 右位, 轉名稱, 韻鏡等 } = this;
    const raw聲 = [...'平上去入'][Math.floor((上位 - 1) / 4)];
    const 韻鏡等漢字 = [...'一二三四'][韻鏡等 - 1];
    return `${轉名稱}·${母位置名稱[右位]}·${raw聲}聲位·韻鏡${韻鏡等漢字}等`;
  }

  /**
   * 轉換為切韻音系音韻地位物件。
   */
  to音韻地位() {
    const { 母, 呼, 切韻等, 類, 韻, 聲 } = this;
    const 當前音韻地位 = new 音韻地位(母, 呼, 切韻等, 類, 韻, 聲);
    return 當前音韻地位;
  }

  /**
   * 判定兩個韻鏡位置是否完全相同。
   */
  等於(other: 韻鏡位置) {
    return this.轉號 === other.轉號 && this.上位 === other.上位 && this.右位 === other.右位;
  }
}

// 右位: 為區分尤/幽韻
const 轉號上位右位2韻 = (轉號: number, 上位: number, 右位: number) => {
  const raw聲 = [...'平上去入'][Math.floor((上位 - 1) / 4)];
  const 韻鏡等 = ((上位 - 1) % 4) + 1;
  const is齒音 = 12 < 右位 && 右位 <= 17;
  const 轉名稱 = `${轉名稱列表[轉號 - 1]}圖`;

  switch (轉號) {
    case 1:
      defaultLogger.log('此位置屬於東韻');
      return '東';
    case 2:
      if (韻鏡等 === 1) {
        if (raw聲 === '上') {
          defaultLogger.log(`${轉名稱}、上聲、韻鏡一等未標註對應韻，實際為冬韻，與其餘三聲相同`);
          return '冬';
        }
        defaultLogger.log('此位置屬於冬韻');
        return '冬';
      }
      defaultLogger.log('此位置屬於鍾韻');
      return '鍾';
    case 3:
      defaultLogger.log('此位置屬於江韻');
      return '江';
    case 4:
    case 5:
      defaultLogger.log('此位置屬於支韻');
      return '支';
    case 6:
    case 7:
      defaultLogger.log('此位置屬於脂韻');
      return '脂';
    case 8:
      defaultLogger.log('此位置屬於之韻');
      return '之';
    case 9:
    case 10:
      if (raw聲 === '入') {
        defaultLogger.log('此位置屬於廢韻');
        return '廢';
      }
      defaultLogger.log('此位置屬於微韻');
      return '微';
    case 11:
      defaultLogger.log('此位置屬於魚韻');
      return '魚';
    case 12:
      if (韻鏡等 === 1) {
        defaultLogger.log('此位置屬於模韻');
        return '模';
      }
      defaultLogger.log('此位置屬於虞韻');
      return '虞';
    case 13:
      if (raw聲 === '入') {
        defaultLogger.log('此位置屬於夬韻');
        return '夬';
      }
      if (韻鏡等 === 1) {
        defaultLogger.log('此位置屬於咍韻');
        return '咍';
      }
      if (韻鏡等 === 2) {
        defaultLogger.log('此位置屬於皆韻');
        return '皆';
      }
      if (韻鏡等 === 4) {
        defaultLogger.log('此位置屬於齊韻');
        return '齊';
      }
      if (韻鏡等 === 3) {
        if (raw聲 === '去') {
          defaultLogger.log('此位置屬於祭韻');
          return '祭';
        }
        defaultLogger.log(`${轉名稱}、平上聲、韻鏡三等未標註對應韻，實際為咍韻`);
        return '咍'; // 咍韻三等平上聲均為特殊字，而咍韻三等去聲恰好無字，該處所排入字全為祭韻字。祭韻字佔用去聲位
      }
      throw new Error(`invalid 韻鏡等 ${韻鏡等}`);
    case 14:
      if (raw聲 === '入') {
        defaultLogger.log('此位置屬於夬韻');
        return '夬';
      }
      if (韻鏡等 === 1) {
        defaultLogger.log('此位置屬於灰韻');
        return '灰';
      }
      if (韻鏡等 === 2) {
        defaultLogger.log('此位置屬於皆韻');
        return '皆';
      }
      if (韻鏡等 === 4) {
        defaultLogger.log('此位置屬於齊韻');
        return '齊';
      }
      if (韻鏡等 === 3) {
        if (raw聲 === '去') {
          defaultLogger.log('此位置屬於祭韻');
          return '祭';
        }
        throw new Error(`invalid combination 轉 14 韻鏡三等${raw聲}聲`); // 祭韻字佔用去聲位
      }
      throw new Error('error');
    case 15:
    case 16:
      if (韻鏡等 === 2) {
        defaultLogger.log('此位置屬於佳韻');
        return '佳';
      }
      if (raw聲 === '去') {
        if (韻鏡等 === 1) {
          defaultLogger.log('此位置屬於泰韻');
          return '泰';
        }
        if (韻鏡等 === 4) {
          defaultLogger.log('此位置屬於祭韻');
          return '祭';
        }
      }
      throw new Error('error');
    case 17:
      if (韻鏡等 === 1) {
        defaultLogger.log('此位置屬於痕韻');
        return '痕';
      }
      if (韻鏡等 === 3 || 韻鏡等 === 4) {
        defaultLogger.log('此位置屬於真韻');
        return '真';
      }
      if (韻鏡等 === 2) {
        defaultLogger.log('此位置屬於臻韻');
        return '臻';
      }
      throw new Error('error');
    case 18:
      if (韻鏡等 === 1) {
        defaultLogger.log('此位置屬於魂韻');
        return '魂';
      }
      defaultLogger.log('此位置屬於真韻');
      return '真';
    case 19:
      defaultLogger.log('此位置屬於殷韻');
      return '殷';
    case 20:
      defaultLogger.log('此位置屬於文韻');
      return '文';
    case 21:
    case 22:
      if (韻鏡等 === 3) {
        defaultLogger.log('此位置屬於元韻');
        return '元';
      }
      if (韻鏡等 === 4) {
        defaultLogger.log('此位置屬於仙韻');
        return '仙';
      }
      if (韻鏡等 === 2) {
        if (raw聲 === '入') {
          defaultLogger.log(`此位置標註為山韻，但${轉名稱}、入聲、韻鏡二等刪、山韻排反，實際為刪韻`);
          return '刪';
        }
        defaultLogger.log('此位置屬於山韻');
        return '山';
      }
      throw new Error('error');
    case 23:
    case 24:
      if (韻鏡等 === 1) {
        defaultLogger.log('此位置屬於寒韻');
        return '寒';
      }
      if (韻鏡等 === 3) {
        defaultLogger.log('此位置屬於仙韻');
        return '仙';
      }
      if (韻鏡等 === 4) {
        defaultLogger.log('此位置屬於先韻');
        return '先';
      }
      if (韻鏡等 === 2) {
        if (raw聲 === '入') {
          defaultLogger.log(`此位置標註為刪韻，但${轉名稱}、入聲、韻鏡二等刪、山韻排反，實際為山韻`);
          return '山';
        }
        defaultLogger.log('此位置屬於刪韻');
        return '刪'; // TODO: 處理仙韻 (see tests)
      }
      throw new Error('error');
    case 25:
      if (韻鏡等 === 1) {
        defaultLogger.log('此位置屬於豪韻');
        return '豪';
      }
      if (韻鏡等 === 2) {
        defaultLogger.log('此位置屬於肴韻');
        return '肴';
      }
      if (韻鏡等 === 3) {
        defaultLogger.log('此位置屬於宵韻');
        return '宵';
      }
      if (韻鏡等 === 4) {
        defaultLogger.log('此位置屬於蕭韻');
        return '蕭';
      }
      throw new Error(`invalid 韻鏡等 ${韻鏡等}`);
    case 26:
      defaultLogger.log('此位置屬於宵韻');
      return '宵';
    case 27:
    case 28:
      defaultLogger.log('此位置屬於歌韻');
      return '歌';
    case 29:
    case 30:
      defaultLogger.log('此位置屬於麻韻');
      return '麻';
    case 31:
    case 32:
      if (韻鏡等 === 1) {
        defaultLogger.log('此位置屬於唐韻');
        return '唐';
      }
      defaultLogger.log('此位置屬於陽韻');
      return '陽';
    case 33:
    case 34:
      if (韻鏡等 === 2 || 韻鏡等 === 3) {
        defaultLogger.log('此位置屬於庚韻');
        return '庚';
      }
      if (韻鏡等 === 4) {
        defaultLogger.log('此位置屬於清韻');
        return '清';
      }
      throw new Error('error');
    case 35:
      if (韻鏡等 === 2) {
        defaultLogger.log('此位置屬於耕韻');
        return '耕';
      }
      if (韻鏡等 === 3) {
        defaultLogger.log('此位置屬於清韻');
        return '清';
      }
      if (韻鏡等 === 4) {
        defaultLogger.log('此位置屬於青韻');
        return '青';
      }
      throw new Error('error');
    case 36:
      if (韻鏡等 === 2) {
        defaultLogger.log('此位置屬於耕韻');
        return '耕';
      }
      if (韻鏡等 === 4) {
        defaultLogger.log('此位置屬於青韻');
        return '青';
      }
      throw new Error('error');
    case 37:
      if (韻鏡等 === 1) {
        defaultLogger.log('此位置屬於侯韻');
        return '侯';
      }
      if (韻鏡等 === 2 || 韻鏡等 === 3) {
        defaultLogger.log('此位置屬於尤韻');
        return '尤';
      }
      if (韻鏡等 === 4) {
        if (is齒音 || 右位 === 21) {
          defaultLogger.log(`此位置標註為幽韻，但${轉名稱}、韻鏡四等位有尤、幽二韻混排，其中齒音與以母為尤韻`);
          return '尤'; // 尤、幽韻在韻鏡四等混排，齒音與以母為尤韻
        }
        defaultLogger.log(`此位置標註為幽韻，但${轉名稱}、韻鏡四等位有尤、幽二韻混排，其中非齒音且非以母為幽韻`);
        return '幽';
      }
      throw new Error(`invalid 韻鏡等 ${韻鏡等}`);
    case 38:
      defaultLogger.log('此位置屬於侵韻');
      return '侵';
    case 39:
      if (韻鏡等 === 1) {
        defaultLogger.log('此位置屬於覃韻');
        return '覃';
      }
      if (韻鏡等 === 2) {
        defaultLogger.log('此位置屬於咸韻');
        return '咸';
      }
      if (韻鏡等 === 3) {
        defaultLogger.log('此位置屬於鹽韻');
        return '鹽';
      }
      if (韻鏡等 === 4) {
        defaultLogger.log('此位置屬於添韻');
        return '添';
      }
      throw new Error(`invalid 韻鏡等 ${韻鏡等}`);
    case 40:
      if (韻鏡等 === 1) {
        defaultLogger.log('此位置屬於談韻');
        return '談';
      }
      if (韻鏡等 === 2) {
        defaultLogger.log('此位置屬於銜韻');
        return '銜';
      }
      if (韻鏡等 === 3) {
        defaultLogger.log('此位置屬於嚴韻');
        return '嚴';
      }
      if (韻鏡等 === 4) {
        defaultLogger.log('此位置屬於鹽韻');
        return '鹽';
      }
      throw new Error(`invalid 韻鏡等 ${韻鏡等}`);
    case 41:
      defaultLogger.log('此位置屬於凡韻');
      return '凡';
    case 42:
    case 43:
      if (韻鏡等 === 1) {
        defaultLogger.log('此位置屬於登韻');
        return '登';
      }
      defaultLogger.log('此位置屬於蒸韻');
      return '蒸';
    default:
      throw new Error('error');
  }
};

export default 韻鏡位置;
